import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FaInfoCircle, FaPlus, FaRegCalendarCheck, FaTiktok, FaYoutube, FaFacebook, FaInstagram, FaTwitter, FaSync, FaCheckSquare, FaRegSquare, FaPinterest } from 'react-icons/fa';
import Textarea from '../../Tools/Textarea';
import Modal from '../../Modal/Modal';
import ConfirmModal from '../../Modal/ConfirmModal';
import CheckboxBtn from '../Checkbox/Checkboxbtn';
import axios from 'axios';
import HorizontalTabs from '../../Navigation/MenuTabs/HorizontalTabs';
import { v4 as uuidv4 } from 'uuid';
import { LuSend } from 'react-icons/lu';
import { getFileType } from '../../../utils/getFileType';
import VideoPreview from './components/Preview';
import UploadArea from './components/UploadArea';
import { Button } from '../../../ui/button';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { MdOutlinePhotoLibrary } from 'react-icons/md';
import { BsThreads } from 'react-icons/bs';
import { CiImageOn } from 'react-icons/ci';
import CustomThumbnailSelector from './components/Thumbnail/CustomThumbnailSelector';
import { IoIosArrowDown } from 'react-icons/io';
import { FaLinkedin } from 'react-icons/fa';
import { FaReddit } from 'react-icons/fa6';
import RedditSettings from './components/RedditSettings';
import TikTokSettings from './components/TikTokSettings';
import PinterestSettings from './components/PinterestSettings';
import CustomDropdown from '../../../ui/CustomDropdown';
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const UploadForm = React.memo(({ draftFormData, onFormAction, uploadType = 'video' }) => {
  const { activeWorkspace, idToken, lockWorkspaceChange, unlockWorkspaceChange } = useWorkspace();
  const [openModal, setOpenModal] = useState(false);
  const [userSocials, setUserSocials] = useState([]);
  const [isModalScheduleOpen, setIsModalScheduleOpen] = useState(false);
  const [socialChecks, setSocialChecks] = useState({
    facebook: false,
    instagram: false,
    tiktok: false,
    youtube: false,
    linkedin: false,
  });
  const [errorMessages, setErrorMessages] = useState([]);
  const [warningMessages, setWarningMessages] = useState([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  const uniquePostId = useMemo(() => uuidv4(), []);
  const [deactivatedFormData, setDeactivatedFormData] = useState([]);



  const siteMenuInitial = useMemo(() => [
    { id: 'draft', label: '', icon: null },
    { id: 'tiktok', label: '', icon: <FaTiktok color='black' /> },
    { id: 'youtube', label: '', icon: <FaYoutube color='red' /> },
    { id: 'instagram', label: '', icon: <FaInstagram color='pink' /> },
    { id: 'facebook', label: '', icon: <FaFacebook color='blue' /> },
    { id: 'x', label: '', icon: <FaTwitter color='black' /> },
    { id: 'threads', label: '', icon: <BsThreads color='black' /> },
    { id: 'linkedin', label: '', icon: <FaLinkedin color='blue' /> },
    { id: 'reddit', label: '', icon: <FaReddit color='red' /> },
    { id: 'pinterest', label: '', icon: <FaPinterest color='red' /> },
  ], []);

  const [siteMenu, setSiteMenu] = useState([]);
  const [activeMenu, setActiveMenu] = useState('draft');

  const [formData, setFormData] = useState([{
    uploadTo: 'draft',
    draftId: '',
    platformId: '',
    postId: uniquePostId,
    title: '',
    description: '',
    uploadType: uploadType,
    privacyStatus: 'public',
    commentEnabled: true,
    isContentDisclosed: false,
    duetEnabled: true,
    stitchEnabled: true,
    isBranded: false,
    isOwnBrand: false,
    hasBeenEdited: false,
    subreddits: [],
    board_ids: [],
    link: '',
    alt_text: '',
    firebasePath: '',
    thumbnailPath: '',
    videoPath: '',
  }]);

  const initialSetupRef = useRef(true);
  const initialLoad = useRef(true);
  const isUpdatingUserSocials = useRef(true);
  const newFormDataRef = useRef(null);



  const getUserSocials = useCallback(async () => {
    if (!idToken || !activeWorkspace.id) return;

    try {
      const response = await axios.get(BACKEND_API_URL + '/api/auth/user/social_media_tokens', {
        params: { workspaceId: activeWorkspace.id },
        headers: { Authorization: `Bearer ${idToken}` },
      });

      response.data.forEach((item) => {
        item.checkbox = false;
      });

      let allowedPlatforms = response.data;

      if (uploadType === 'post') {
        allowedPlatforms = response.data.filter(
          (item) =>
            item.platform === 'facebook' || item.platform === 'instagram' || item.platform === 'pinterest' || item.platform === 'x' || item.platform === 'threads' || item.platform === 'tiktok' || item.platform === 'linkedin' || item.platform === 'reddit'
        );
      } else {
        // Remove Reddit from the allowed platforms video
        allowedPlatforms = response.data.filter(item => item.platform !== 'reddit');
      }

      for (const social of response.data) {
        if (social.platform in socialChecks) {
          setSocialChecks(prev => ({ ...prev, [social.platform]: social.checkbox }));
        }
      }
      setUserSocials(allowedPlatforms);
      return response.data;
    } catch (error) {
      console.error('Error fetching social media tokens:', error);
    }
  }, [idToken, activeWorkspace.id, uploadType, socialChecks]);

  const handleRefreshSocials = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await getUserSocials();
    } catch (error) {
      console.error('Error refreshing social media tokens:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [getUserSocials]);



  // Listen for localStorage changes from other tabs (social auth completion)
  useEffect(() => {
    // Function to check if social auth was successful
    const checkAuthSuccess = () => {
      const authSuccess = localStorage.getItem('social_auth_success');
      if (authSuccess) {
        // Clear the flag
        localStorage.removeItem('social_auth_success');
        // Refresh the socials list
        getUserSocials();
      }
    };

    // Function to handle focus events for pending auth
    const handleFocus = () => {
      const pendingAuth = localStorage.getItem('social_auth_pending');
      if (pendingAuth) {
        // When the user returns to this tab, check if there are pending auths and refresh
        getUserSocials();
        localStorage.removeItem('social_auth_pending');
      }
    };

    // Check immediately (for when returning to an already-open tab)
    checkAuthSuccess();

    // Set up event listener for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'social_auth_success') {
        checkAuthSuccess();
      }
    };

    // Listen for when the window regains focus
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    
    // Clean up the event listeners
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [getUserSocials]);

  useEffect(() => {
    lockWorkspaceChange();
    if (initialSetupRef.current && activeWorkspace.id && idToken) {
      // Fetch initial user socials
      getUserSocials();
      initialSetupRef.current = false;
    }
    return () => { };
  }, [getUserSocials, activeWorkspace.id, idToken]);

  // DONE EFFECTIVIZING



  const insertDraftMenu = useCallback((data) => {
    const containsDraftStatus = data.some(data => data.status === 'draft');
    if (containsDraftStatus) {
      setSiteMenu(prevSiteMenu => {
        const draftTab = siteMenuInitial.find(menuItem => menuItem.id === 'draft');
        if (draftTab && !prevSiteMenu.some(menuItem => menuItem.id === 'draft')) {
          return [...prevSiteMenu, draftTab];
        }
        return prevSiteMenu;
      });
    }
  }, [siteMenuInitial, setSiteMenu]);



  useEffect(() => {
    if (initialLoad.current && draftFormData && draftFormData.length > 0) {
      const hasMatchingPlatform = draftFormData.some(draft =>
        userSocials.some(social => social.platform_id === draft.platformId || draft.uploadTo === 'draft')
      );


      if (hasMatchingPlatform) {
        const updatedDraftFormData = draftFormData.map((data) => ({

          ...data,
          postId: uniquePostId,
        }));
        setFormData(updatedDraftFormData);
        insertDraftMenu(updatedDraftFormData);
        initialLoad.current = false;
      }
    }
  }, [draftFormData, uniquePostId, insertDraftMenu, userSocials]);

  const handleSitieMenuChange = (newTab) => {
    setActiveMenu(newTab);
  };



  useEffect(() => {
    if (isUpdatingUserSocials.current && userSocials.length > 0 && draftFormData.length > 0) {
      isUpdatingUserSocials.current = false;
      const updatedUserSocials = userSocials.map(userSocial => {
        const isMatched = draftFormData.some(upload => upload.platformId === userSocial.platform_id);

        if (userSocial.platform in socialChecks) {
          setSocialChecks(prev => ({ ...prev, [userSocial.platform]: isMatched }));
        }


        return { ...userSocial, checkbox: isMatched };
      });

      // Compare arrays without using JSON.stringify
      const areArraysDifferent = updatedUserSocials.some((item, index) =>
        item.checkbox !== userSocials[index]?.checkbox ||
        item.platform_id !== userSocials[index]?.platform_id
      );

      if (areArraysDifferent) {
        setUserSocials(updatedUserSocials);
      }
    }
  }, [draftFormData, userSocials, socialChecks]);


  useEffect(() => {
    if (userSocials.length > 0) {
      const atLeastOneChecked = userSocials.some((social) => social.checkbox === true);
      let isDraft = false;

      if (draftFormData && draftFormData.length > 0) {
        isDraft = draftFormData.some(upload => upload.uploadTo === 'draft');
      }

      let updatedSiteMenu = [];

      if (atLeastOneChecked) {
        updatedSiteMenu = siteMenuInitial
          .filter(menuItem =>
            userSocials.some(social => social.platform === menuItem.id && social.checkbox === true)
          )
          .flatMap(menuItem => {
            const matchedSocials = userSocials.filter(
              social => social.platform === menuItem.id && social.checkbox === true
            );

            const matched = matchedSocials.map(social => ({
              ...menuItem,
              id: social.platform_id,
              label: `${social.username.substring(0, 6)}..`
            }));
            return matched;
          });
      }
      if (!atLeastOneChecked || isDraft) {
        const draftTab = { id: 'draft', label: 'Draft', icon: null };
        if (!updatedSiteMenu.some(menuItem => menuItem.id === 'draft')) {
          updatedSiteMenu.push(draftTab);
        }
      }

      // Compare menus without using JSON.stringify
      const areMenusDifferent =
        updatedSiteMenu.length !== siteMenu.length ||
        updatedSiteMenu.some((item, index) =>
          item.id !== siteMenu[index]?.id ||
          item.label !== siteMenu[index]?.label
        );

      if (areMenusDifferent) {
        setSiteMenu(updatedSiteMenu);
      }

      const isActiveMenuValid = updatedSiteMenu.some(menuItem => menuItem.id === activeMenu);

      if (!isActiveMenuValid) {
        if (updatedSiteMenu.some(menuItem => menuItem.id === 'draft')) {
          if (activeMenu !== 'draft') {
            setActiveMenu('draft');
          }
        } else if (updatedSiteMenu.length > 0) {
          if (activeMenu !== updatedSiteMenu[0].id) {
            setActiveMenu(updatedSiteMenu[0].id);
          }
        } else {
          if (activeMenu !== '') {
            setActiveMenu('');
          }
        }
      }
    }
  }, [draftFormData, userSocials, siteMenuInitial, activeMenu, siteMenu]);


  // Define handleActionClick before using it in useEffect
  const proceedWithAction = useCallback(async (action) => {
    setIsActionInProgress(true);
    
    try {
      let editedFormData = formData.filter(
        (data) => data.hasBeenEdited || data.description !== ''
      );

      if (editedFormData.length >= 2) {
        editedFormData = editedFormData.filter((data) => data.uploadTo !== 'draft');
      }
      await onFormAction(action, editedFormData);
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setIsActionInProgress(false);
    }
  }, [formData, onFormAction]);

  const handleActionClick = useCallback(async (action) => {
    // For 'upload', check warnings immediately
    if (action === 'upload' && warningMessages.length > 0 && errorMessages.length === 0) {
      setActionToConfirm(action);
      setIsConfirmModalOpen(true);
    } else if (action !== 'schedule') { // For 'draft' or 'upload' with errors/no warnings
      await proceedWithAction(action);
    }
    // 'schedule' action is handled by the useEffect below
  }, [warningMessages, errorMessages, proceedWithAction]);

  const closeModal = (action) => {
    if (action instanceof Date) {
      const newFormData = formData.map((data) => ({
        ...data,
        schedule: action,
      }));
      setFormData(newFormData)
      newFormDataRef.current = newFormData;
    }

    setOpenModal(false);
    setIsModalScheduleOpen(false);
  };

  useEffect(() => {
    if (newFormDataRef.current) { // This means a schedule date was selected
      if (warningMessages.length > 0 && errorMessages.length === 0) {
        setActionToConfirm('schedule');
        setIsConfirmModalOpen(true);
        // newFormDataRef.current will be cleared on confirm/cancel
      } else {
        // If errors exist, proceedWithAction won't effectively publish/schedule due to disabled buttons,
        // but this logic ensures newFormDataRef.current is cleared.
        // Or if no warnings and no errors, proceed.
        
        // Clear the ref immediately to prevent multiple executions
        newFormDataRef.current = null;
        
        const executeSchedule = async () => {
          await proceedWithAction('schedule');
        };
        executeSchedule();
      }
    }
  }, [formData, warningMessages, errorMessages, proceedWithAction]); // formData also triggers this, then we check warnings

  const handleFormDataChange = useCallback((platform, newFormData) => {
    setFormData(prevFormData =>
      prevFormData.map(data =>
        data.platformId === platform || data.uploadTo === platform
          ? { ...data, ...newFormData, hasBeenEdited: true }
          : data
      )
    );
  }, []);

  const handleMediaSelect = useCallback((newFormData) => {
    // Update all formData objects with the new media paths
    setFormData(prevFormData =>
      prevFormData.map(data => ({
        ...data,
        ...newFormData,
        hasBeenEdited: true,
      }))
    );
  }, []);

  const socialCheckboxesActivate = useCallback((userSocial) => {
    setUserSocials((prevUserSocials) => {
      // Toggle the checkbox state for the selected social platform
      const updatedUserSocials = prevUserSocials.map((social) => {
        if (social.platform_id === userSocial.platform_id) {
          return { ...social, checkbox: !social.checkbox };
        }
        return social;
      });

      // Update socialChecks state for the toggled platform
      if (userSocial.platform in socialChecks) {
        setSocialChecks((prev) => ({ ...prev, [userSocial.platform]: !userSocial.checkbox }));
      }

      // Update formData based on the checkbox state
      setFormData((prevFormData) => {
        // Store the current form data before making changes (for preserving data when creating draft fallback)
        const currentFormData = prevFormData[0] || {};
        
        if (!userSocial.checkbox) {
          // If the checkbox is now checked, add the platform data back to formData
          const existingData = deactivatedFormData.find(data => data.platformId === userSocial.platform_id);
          if (existingData) {
            const existingObj = prevFormData.find(data => data.platformId === userSocial.platform_id);
            if (!existingObj) {
              prevFormData = [...prevFormData, existingData];
              setDeactivatedFormData((prev) => prev.filter(data => data.platformId !== userSocial.platform_id));
            }
          } else {
            const existingObj = prevFormData.find(data => data.platformId === userSocial.platform_id);
            if (!existingObj) {
              // Copy data from the first object in formData (which has the most complete data)
              const firstFormData = prevFormData[0] || {};
              prevFormData = [...prevFormData, {
                uploadTo: userSocial.platform,
                draftId: firstFormData.draftId || '',
                postId: uniquePostId,
                title: firstFormData.title || '',
                description: firstFormData.description || '',
                uploadType: uploadType,
                privacyStatus: 'public',
                commentEnabled: true,
                isContentDisclosed: false,
                duetEnabled: true,
                stitchEnabled: true,
                isBranded: false,
                isOwnBrand: false,
                hasBeenEdited: false,
                platformId: userSocial.platform_id,
                comment: firstFormData.comment || '',
                subreddits: firstFormData.subreddits || [],
                board_ids: firstFormData.board_ids || [],
                firebasePath: firstFormData.firebasePath || '',
                thumbnailPath: firstFormData.thumbnailPath || '',
                videoPath: firstFormData.videoPath || '',
                link: firstFormData.link || '',
                alt_text: firstFormData.alt_text || '',
              }];
            }
          }
        } else {
          // If the checkbox is now unchecked, remove the platform data from formData
          const dataToDeactivate = prevFormData.find(data => data.uploadTo === userSocial.platform);
          if (dataToDeactivate) {
            setDeactivatedFormData((prev) => [...prev, dataToDeactivate]);
            prevFormData = prevFormData.filter(data => data.platformId !== userSocial.platform_id);
          }
        }

        // Ensure there's always at least one draft entry in formData
        if (prevFormData.length === 0) {
          prevFormData.push({
            uploadTo: 'draft',
            draftId: currentFormData.draftId || '',
            postId: uniquePostId,
            title: currentFormData.title || '',
            description: currentFormData.description || '',
            uploadType: uploadType,
            privacyStatus: 'public',
            commentEnabled: true,
            isContentDisclosed: false,
            duetEnabled: true,
            stitchEnabled: true,
            isBranded: false,
            isOwnBrand: false,
            hasBeenEdited: false,
            platformId: '',
            subreddits: currentFormData.subreddits || [],
            board_ids: currentFormData.board_ids || [],
            firebasePath: currentFormData.firebasePath || '',
            thumbnailPath: currentFormData.thumbnailPath || '',
            videoPath: currentFormData.videoPath || '',
            link: currentFormData.link || '',
            alt_text: currentFormData.alt_text || '',
            comment: currentFormData.comment || '',
            schedule: currentFormData.schedule || null,
          });
        }

        // Update postId for all entries in formData
        return prevFormData.map((data) => ({
          ...data,
          postId: uniquePostId,
        }));
      });

      return updatedUserSocials;
    });
  }, [socialChecks, deactivatedFormData, uniquePostId, uploadType]);

  const findPlatformData = (platform) => {
    return formData.find(data => data.platformId === platform) || formData[0];
  };

  const handleErrors = useCallback((errors, warnings) => {
    setErrorMessages(errors || []);
    setWarningMessages(warnings || []);
  }, []);



  // Helper function for warning colors (can be moved to a utility file if used elsewhere)
  const getWarningDisplayColor = (color) => {
    switch (color) {
      case 'red': return '#FEE2E2'; // bg-red-100
      case 'yellow': return '#FEF3C7'; // bg-yellow-100
      default: return '#E5E7EB'; // bg-gray-200 (fallback)
    }
  };

  const handleConfirm = async () => {
    if (actionToConfirm) {
      await proceedWithAction(actionToConfirm);
    }
    if (actionToConfirm === 'schedule') {
      newFormDataRef.current = null; // Clear ref if action was schedule
    }
    setIsConfirmModalOpen(false);
    setActionToConfirm(null);
  };

  const handleCancelConfirm = () => {
    if (actionToConfirm === 'schedule') {
      // Clear the schedule from formData if it was set by closeModal
      setFormData(prevFormData =>
        prevFormData.map(data => {
          const { schedule, ...rest } = data; // Remove schedule property
          return rest;
        })
      );
      newFormDataRef.current = null; // Important to clear this ref
    }
    setIsConfirmModalOpen(false);
    setActionToConfirm(null);
  };

  return (
    <>
      <div className='flex flex-col h-full pb-1 overflow-y-auto overflow-x-hidden'>
        <div className='flex flex-col md:flex-row w-full'>
          {/* LEFT PANEL */}
          <div className={`p-2 !w-full md:w-3/5 md:min-w-0`}>
            <div className='my-4'>
              <HorizontalTabs tabs={siteMenu} onTabChange={handleSitieMenuChange} activeTab={activeMenu} />
            </div>

            <div className='w-full'>
              <Form
                formData={findPlatformData(activeMenu)}
                onFormDataChange={(newFormData) => handleFormDataChange(activeMenu, newFormData)}
                onMediaSelect={handleMediaSelect}
                userSocials={userSocials}
                uploadType={uploadType}
                socialCheckboxesActivate={socialCheckboxesActivate}
                setOpenModal={setOpenModal}
                handleRefreshSocials={handleRefreshSocials}
                isRefreshing={isRefreshing}
                draftFormData={draftFormData}
                siteMenuInitial={siteMenuInitial}
              />

              {socialChecks.tiktok && formData.uploadTo === 'tiktok' && (
                <TikTokPolicy formData={formData} />
              )}
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className='!w-full md:!w-2/5 md:border-l md-border-l-0 p-5 md:border-t-0 border-t border-gray-300 min-w-[400px]'>
            <VideoPreview
              uploadType={uploadType}
              formData={formData}
              formTabs={siteMenu}
              userSocials={userSocials}
              activeMenuForm={activeMenu}
              onError={handleErrors}
            />
          </div>
        </div>


        <ActionButtons
          handleActionClick={handleActionClick}
          setIsModalScheduleOpen={setIsModalScheduleOpen}
          disableButtons={errorMessages.length > 0}
          isActionInProgress={isActionInProgress}
        />
      </div>


      {/* MODALS */}
      {isModalScheduleOpen && (
        <Modal
          open={isModalScheduleOpen}
          otherInfo={formData[0].schedule ? new Date(formData[0].schedule) : new Date()}
          closeModal={closeModal}
          type={'ScheduledModal'}
        />
      )}
      {openModal && (
        <Modal
          open={openModal}
          closeModal={closeModal}
          type={'ConnectSocialsModal'}
        />
      )}
      {isConfirmModalOpen && (
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onClose={handleCancelConfirm}
          onConfirm={handleConfirm}
          title="Confirm Action"
          message={
            <>
              <p>The following warnings were found. Do you want to proceed with {actionToConfirm}ing?</p>
              <div className="mt-2 text-sm max-h-60 overflow-y-auto">
                {warningMessages.map((warning, index) => (
                  <div key={index} className="flex items-center gap-2 mt-1 p-2 rounded text-gray-700" style={{ backgroundColor: getWarningDisplayColor(warning.color) }}>
                    {warning.icon && React.createElement(warning.icon, { size: 20, className: "flex-shrink-0" })}
                    <span>{warning.message}</span>
                  </div>
                ))}
              </div>
            </>
          }
        />
      )}
    </>
  );
});

const SocialMediaCheckboxes = React.memo(({
  userSocials,
  socialCheckboxesActivate,
  setOpenModal,
  handleRefreshSocials,
  isRefreshing,
  draftFormData,
  siteMenuInitial,
  uploadType
}) => {

  const connectedPlatformIds = useMemo(() => new Set(userSocials.map(s => s.platform_id)), [userSocials]);

  const handleToggleAllSocials = () => {
    if (userSocials.length === 0) return;

    const areAllCurrentlyChecked = userSocials.every(social => social.checkbox);

    userSocials.forEach(social => {
      // If we want to uncheck all (because all are currently checked), and this one is checked, call activate
      if (areAllCurrentlyChecked && social.checkbox) {
        socialCheckboxesActivate(social);
      }
      // If we want to check all (because not all are currently checked), and this one is not checked, call activate
      else if (!areAllCurrentlyChecked && !social.checkbox) {
        socialCheckboxesActivate(social);
      }
    });
  };

  const disconnectedDrafts = useMemo(() => {
    if (!draftFormData || !Array.isArray(draftFormData) || draftFormData.length === 0 || !siteMenuInitial) {
      return [];
    }
    // Use a Set to track unique disconnected platformIds from the draft
    const uniqueDisconnectedPlatformIds = new Set();
    const result = [];

    draftFormData.forEach(draft => {
      // Check if platformId exists, is not 'draft', not connected, and not already added
      if (draft.platformId &&
        draft.uploadTo !== 'draft' &&
        !connectedPlatformIds.has(draft.platformId) &&
        !uniqueDisconnectedPlatformIds.has(draft.platformId)) {
        const siteInfo = siteMenuInitial.find(menu => menu.id === draft.uploadTo);
        result.push({
          platform_id: draft.platformId,
          platform: draft.uploadTo,
          // Provide more context in the name for disconnected state
          username: `Connect ${draft.uploadTo}`,
          // Get logo from siteMenuInitial, handle cases where it might not be found
          logo: siteInfo ? siteInfo.icon : <FaInfoCircle />, // Fallback icon
          disconnected: true, // Mark as disconnected
        });
        uniqueDisconnectedPlatformIds.add(draft.platformId); // Add to set to prevent duplicates
      }
    });
    return result;
  }, [draftFormData, connectedPlatformIds, siteMenuInitial]);


  return (
    <div className={`flex flex-col mb-5 dashboard-container !p-0`}>
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-2'>
          <p className='text-md font-semibold'>Select socials you want to upload to</p>
          <span className='text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full'>
            {uploadType === 'post' ? 'Post/Image Mode' : 'Video Mode'}
          </span>
        </div>
        <div className='flex gap-2 items-center'>
          {userSocials.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleAllSocials}
            >
              {userSocials.every(s => s.checkbox) ? <FaCheckSquare size={14} /> : <FaRegSquare size={14} />}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={isRefreshing ? null : handleRefreshSocials}
            disabled={isRefreshing}
          >
            <FaSync size={10} className={isRefreshing ? 'animate-spin' : ''} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenModal(true)}
          >
            <FaPlus size={10} />
            <span className='text-[11px]'>Connect</span>
          </Button>
        </div>
      </div>
      <div className={`flex w-full gap-4 px-1 py-2 overflow-x-auto overflow-y-hidden flex-wrap`}>
        {userSocials.length > 0 && userSocials.map((socialMedia, index) => (
          <div
            key={socialMedia.platform_id || index}
            className={`${socialMedia.checkbox ? 'opacity-100' : 'opacity-40'} relative transition-transform transform hover:scale-105 hover:opacity-100`}
            onClick={() => socialCheckboxesActivate(socialMedia)}
          >
            <CheckboxBtn
              name={socialMedia.username}
              platform={socialMedia.platform}
              logo={socialMedia.logo}
              active={socialMedia.checkbox}
            />
          </div>
        ))}

        {disconnectedDrafts.map((disconnectedSocial, index) => (
          <div
            key={disconnectedSocial.platform_id || `disconnected-${index}`}
            className={`opacity-60 relative transition-transform transform hover:scale-105 cursor-pointer`}
            onClick={() => setOpenModal(true)}
            title={`This ${disconnectedSocial.platform} social is not connected. Click to connect.`}
          >
            <CheckboxBtn
              name={disconnectedSocial.username}
              platform={disconnectedSocial.platform}
              logo={disconnectedSocial.logo}
              active={false}
              disconnected={true}
            />
          </div>
        ))}

        {userSocials.length === 0 && disconnectedDrafts.length === 0 && (
          <div className='flex flex-col items-center justify-center w-full h-full gap-3'>
            <p className='text-gray-500 text-center mt-3'>No socials connected</p>
            <p className='text-gray-500 italic text-sm text-center opacity-70'>Connect your socials to start uploading or continue with a draft</p>
          </div>
        )}
      </div>
    </div>
  );
});

const ActionButtons = React.memo(({ handleActionClick, setIsModalScheduleOpen, disableButtons, isActionInProgress }) => {
  const isDisabled = disableButtons || isActionInProgress;
  
  return (
    <div className='flex py-3 relative items-end border-t px-5 overflow-hidden'>
      <div className='flex gap-2 items-end justify-end w-full'>
        <Button
          variant="outline"
          size="xl"
          className="border-b border-gray-200 px-2 text-gray-700"
          onClick={() => {
            handleActionClick('draft');
          }}
          disabled={isActionInProgress}
        >
          {isActionInProgress ? (
            <>
              <FaSync className="animate-spin mr-2" size={14} />
              Saving...
            </>
          ) : (
            'Save Draft'
          )}
        </Button>
        <Button
          variant="secondary"
          size="xl"
          className={`flex gap-1 items-center ${isDisabled ? 'cursor-not-allowed bg-gray-300' : ''}`}
          onClick={() => {
            if (!isDisabled) {
              setIsModalScheduleOpen(true);
            }
          }}
          disabled={isDisabled}
        >
          {isActionInProgress ? (
            <>
              <FaSync className="animate-spin" size={14} />
              Processing...
            </>
          ) : (
            <>
              Schedule
              <FaRegCalendarCheck />
            </>
          )}
        </Button>

        <Button
          variant="default"
          size="xl"
          className={`flex gap-1 items-center ${isDisabled ? 'cursor-not-allowed bg-gray-300' : ''}`}
          onClick={() => {
            if (!isDisabled) {
              handleActionClick('upload');
            }
          }}
          disabled={isDisabled}
        >
          {isActionInProgress ? (
            <>
              <FaSync className="animate-spin" size={14} />
              Publishing...
            </>
          ) : (
            <>
              Publish
              <LuSend />
            </>
          )}
        </Button>
      </div>

    </div>
  );
});

const TikTokPolicy = React.memo(({ formData }) => (
  <div className="text-[11px] mt-2">
    {!formData.isBranded ? (
      <span>* By posting, you agree to TikTok's <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer" style={{ color: "#FE2C55" }}>Music Usage Confirmation</a></span>
    ) : (
      <span>* By posting, you agree to TikTok's <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer" style={{ color: "#FE2C55" }}>Brand Content Policy</a> and <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer" style={{ color: "#FE2C55" }}>Music Usage Confirmation</a></span>
    )}
  </div>
));

const Form = React.memo(({
  formData,
  onFormDataChange,
  onMediaSelect,
  userSocials,
  uploadType,
  socialCheckboxesActivate,
  setOpenModal,
  handleRefreshSocials,
  isRefreshing,
  draftFormData,
  siteMenuInitial,
}) => {
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [isThumbnailSelectorOpen, setIsThumbnailSelectorOpen] = useState(false);
  const [isUploadAreaVisible, setIsUploadAreaVisible] = useState(false);


  const handleInternalFileSelect = (firebasePath, thumbnailPath) => {
    let localPath = '';
    if (firebasePath.includes('firebase')) {
      localPath = firebasePath.split('/').pop();
    } else {
      localPath = null;
    }

    const newFormData = {
      firebasePath: firebasePath,
      thumbnailPath: thumbnailPath,
      videoPath: localPath,
      hasBeenEdited: true,
    };

    // Use the onMediaSelect callback to update all formData objects
    onMediaSelect(newFormData);
  };

  const closeLibraryModal = (path) => {
    setIsLibraryModalOpen(false);
    if (typeof path === 'string' && path.trim() !== '') {
      handleInternalFileSelect(path, null);
    }
  };

  const handleInputChange = (field, value) => {
    const newFormData = { [field]: value };
    onFormDataChange(newFormData);
  };

  const [isInputFocused, setIsInputFocused] = useState(false);

  const handleDescriptionChange = (value) => {
    handleInputChange('description', value);
  };

  const toggleDisclosure = () => {
    if (!formData.isContentDisclosed) {
      handleInputChange('isOwnBrand', false);
      handleInputChange('privacyStatus', false);
    }
    handleInputChange('isContentDisclosed', !formData.isContentDisclosed);
  };

  const toggleBranded = () => {
    handleInputChange('isBranded', !formData.isBranded);
    if (!formData.isBranded) {
      handleInputChange('privacyStatus', 'public');
    }
  };

  const handleCommentChange = (value) => {
    handleInputChange('comment', value);
  };

  const handleAIImageGenerated = (imageData) => {
    // Use the onMediaSelect callback to update all formData objects with the AI generated image
    onMediaSelect(imageData);
  };

  const handleThumbnailSelect = (img) => {
    handleInternalFileSelect(formData.firebasePath, img.imageUrl);
  };

  const toggleThumbnailSelector = () => {
    setIsThumbnailSelectorOpen(!isThumbnailSelectorOpen);
  };

  return (
    <>
      <form onClick={(e) => e.stopPropagation()}>
        <div className={``}>
          {((getFileType(formData.uploadType) === 'video' && (formData.uploadTo === 'facebook' || formData.uploadTo === 'youtube')) || formData.uploadTo === 'reddit' || formData.uploadTo === 'pinterest') && (
            <div className='py-2 w-full lg:!w-2/3 mx-auto'>
              <Textarea
              onDescriptionChange={(value) => handleInputChange('title', value)}
              initialDescription={formData.title}
              placeholder={'Write your title'}
              formData={formData}
              rows={1}
              maxLength={100}
              hideBottomBar={true}
              onAIImageGenerated={handleAIImageGenerated}
            />
            </div>
          )}
          <div className='py-2 w-full lg:!w-2/3 mx-auto'>
            <Textarea onDescriptionChange={handleDescriptionChange} initialDescription={formData.description}
              placeholder={'Write your ' + (formData.uploadTo === 'youtube' ? 'description' : 'caption')}
              formData={formData}
              handlePrivacyStatusChange={handleInputChange}
              rows={8}
              maxLength={formData.uploadTo === 'x' ? 280 : formData.uploadTo === 'youtube' ? 3000 : formData.uploadTo === 'facebook' ? 420 : 
                formData.uploadTo === 'tiktok' ? 2200 : formData.uploadTo === 'instagram' ? 2200 : formData.uploadTo === 'reddit' ? 20000 : 
                formData.uploadTo === 'pinterest' ? 2048 : 500}
              onAIImageGenerated={handleAIImageGenerated}
            />
          </div>

          <div className='border-b border-t py-2 border-gray-300 mb-4 w-full xl:w-2/3 mx-auto'>
            <SocialMediaCheckboxes
              userSocials={userSocials}
              socialCheckboxesActivate={socialCheckboxesActivate}
              setOpenModal={setOpenModal}
              handleRefreshSocials={handleRefreshSocials}
              isRefreshing={isRefreshing}
              draftFormData={draftFormData}
              siteMenuInitial={siteMenuInitial}
              uploadType={uploadType}
            />
          </div>

          <div className='flex w-full gap-5 lg:!w-2/3 mx-auto'>
            {getFileType(formData.uploadType) === 'video' && formData.uploadTo === 'facebook' && (
              <CustomDropdown
                label="Type:"
                value={formData.uploadType}
                onChange={(value) => handleInputChange('uploadType', value)}
                options={[
                  { label: 'Video Post', value: 'video' },
                  { label: 'Reel', value: 'reel' },
                ]}
                className="w-1/2 mt-5"
              />
            )}

            {getFileType(formData.uploadType) === 'video' && formData.uploadTo === 'instagram' && (
              <CustomDropdown
                label="Type:"
                value={formData.uploadType}
                onChange={(value) => handleInputChange('uploadType', value)}
                options={[
                  { label: 'Reels', value: 'REELS' },
                  { label: 'Story', value: 'STORIES' },
                ]}
                className="w-1/2 mt-5"
              />
            )}
          </div>

          {formData.uploadTo === 'tiktok' && (
            <div className='w-full lg:w-2/3 mx-auto'>
              <TikTokSettings
              formData={formData}
              handleInputChange={handleInputChange}
              toggleDisclosure={toggleDisclosure}
              toggleBranded={toggleBranded}
                userSocials={userSocials}
              />
            </div>
          )}

          {formData.uploadTo === 'reddit' && (
            <div className='w-full lg:w-2/3 mx-auto'>
            <RedditSettings
              formData={formData}
              handleInputChange={handleInputChange}
              userSocials={userSocials}
            />
            </div>
          )}

          {formData.uploadTo === 'pinterest' && (
            <div className='w-full lg:w-2/3 mx-auto'>
            <PinterestSettings
              formData={formData}
              handleInputChange={handleInputChange}
              userSocials={userSocials}
            />
            </div>
          )}
        </div>

        <div className="py-2 mx-auto flex flex-col justify-center items-center w-full gap-1 w-full lg:w-2/3">
          {formData.firebasePath && (
            <Button
              variant="outline"
              type="button"
              className='self-end flex items-center gap-1 mb-2 w-full'
              onClick={() => setIsUploadAreaVisible(!isUploadAreaVisible)}
            >
              <FaPlus size={12} className={isUploadAreaVisible ? 'rotate-45 transition-transform' : 'transition-transform'} />
              <span className='text-sm'>Change Media</span>
              <IoIosArrowDown size={15} className={isUploadAreaVisible ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </Button>
          )}

          {(!formData.firebasePath || isUploadAreaVisible) && (
            <>
              <div className='flex gap-2 justify-end w-full'>
                <Button variant="primary" size="md" type="button" className='self-end' onClick={() => setIsLibraryModalOpen(true)}>
                  <MdOutlinePhotoLibrary size={15} />
                  <span className='text-sm text-white'>My Library</span>
                </Button>
              </div>
              <UploadArea onFileSelect={handleInternalFileSelect} uploadType={uploadType} />
            </>
          )}
        </div>

        {formData.uploadTo !== 'tiktok' && formData.uploadTo !== 'linkedin' && formData.uploadTo !== 'pinterest' && (
          <div className='py-2 w-full lg:w-2/3 mx-auto'>
            <Textarea showThreeDots={false} showPrivacy={false} onDescriptionChange={handleCommentChange} initialDescription={formData.comment} placeholder='Add a comment' formData={formData} rows={1} onAIImageGenerated={handleAIImageGenerated} />
          </div>
        )}

        {/* Thumbnail selector section */}
        {formData.firebasePath && getFileType(uploadType) === 'video' && (
          <div className='py-2 w-full lg:w-2/3 mx-auto'>
            <Button
              variant="outline"
              size="md"
              type="button"
              className='flex items-center gap-2 w-full'
              onClick={toggleThumbnailSelector}
            >
              <CiImageOn size={15} />
              <span className='text-sm'>{isThumbnailSelectorOpen ? 'Close Thumbnail Selector' : 'Edit Thumbnail'}</span>
              <IoIosArrowDown size={15} className={isThumbnailSelectorOpen ? 'rotate-180' : ''} />
            </Button>

            {isThumbnailSelectorOpen && (
              <div className="mt-3">
                <CustomThumbnailSelector
                  videoPath={formData.firebasePath}
                  onThumbnailSelect={handleThumbnailSelect}
                  currentThumbnail={formData.thumbnailPath}
                />
              </div>
            )}
          </div>
        )}

      </form>

      {isLibraryModalOpen && (
        <Modal
          open={isLibraryModalOpen}
          closeModal={closeLibraryModal}
          type={'LibraryModal'}
          otherInfo={uploadType}
        />
      )}
    </>
  );
});



export default UploadForm;
